import type { CityConfig } from "../city-template/types";

export const rosevilleConfig: CityConfig = {
  city: "Roseville",
  state: "California",
  stateAbbr: "CA",
  slug: "roseville",
  route: "/cities/roseville",
  title: "Emergency Passport Roseville, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Roseville, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Placer County and Sacramento metro travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Roseville, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Roseville and Placer County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Roseville%20California%20suburban%20cityscape%20at%20golden%20hour%20with%20modern%20shopping%20district%20and%20green%20tree-lined%20streets%2C%20Sierra%20Nevada%20foothills%20visible%20in%20the%20far%20background%2C%20warm%20sunset%20light%20over%20Placer%20County%2C%20clean%20modern%20suburban%20architecture%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20suburban%20environment&width=1800&height=700&seq=roseville-hero-001&orientation=landscape",
  heroImageAlt: "Roseville California suburban cityscape at golden hour with modern downtown and Placer County foothills",
  geoPosition: "38.7521;-121.2880",
  geoPlacename: "Roseville, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in downtown SF — approximately 90 minutes from Roseville via I-80",
  },
  nearbyCitiesZips: [
    { name: "Roseville", zips: "95661, 95678, 95747" },
    { name: "Rocklin", zips: "95677, 95765" },
    { name: "Lincoln", zips: "95648" },
    { name: "Folsom", zips: "95630" },
    { name: "Citrus Heights", zips: "95610, 95611, 95621" },
    { name: "Granite Bay", zips: "95746" },
    { name: "Auburn", zips: "95602, 95603, 95604" },
    { name: "Elk Grove", zips: "95624, 95757, 95758" },
    { name: "Sacramento", zips: "95814–95835" },
    { name: "Rancho Cordova", zips: "95670, 95742" },
    { name: "Loomis", zips: "95650" },
    { name: "Natomas", zips: "95833, 95834, 95835" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Placer County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Stockton", route: "/cities/stockton" },
    { name: "Santa Rosa", route: "/cities/santa-rosa" },
    { name: "Fresno", route: "/cities/fresno" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Roseville remotely via secure courier. We do not have a walk-in office in Roseville — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Placer County travelers.",
  fpcOfficeDistance: "Serving all of Placer County and Sacramento metro",
  serviceAreaIntro: "From the Roseville Galleria area to the Sierra College corridor, from Fiddyment Ranch to the historic Old Town — Fast Passport Center serves the entire Roseville community. We regularly help travelers from Rocklin, Lincoln, Folsom, and Citrus Heights get their passports fast.",
  nearbyCitiesIntro: "We serve all Roseville neighborhoods and nearby Placer and Sacramento County cities including Rocklin, Lincoln, Folsom, Citrus Heights, Granite Bay, and Auburn. If you are in the Sacramento metro area, we have you covered.",
  howItWorksIntro: "Most Roseville travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Sacramento International Airport (SMF) nearby and Bay Area airports accessible, Roseville travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Roseville and Sacramento metro travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Roseville? Here are the most common ones we hear from Placer County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Placer County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jessica W.",
      location: "Roseville, CA",
      rating: 5,
      text: "My cruise to Alaska was in 4 days and I realized my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved our family vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Michael P.",
      location: "Rocklin, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Toronto. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Amanda T.",
      location: "Folsom, CA",
      rating: 5,
      text: "Lost my passport the week before my wedding in Jamaica. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Steven L.",
      location: "Granite Bay, CA",
      rating: 5,
      text: "As a frequent international traveler from the Sacramento area, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "roseville-1",
      name: "Roseville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1730 Santa Clara Dr",
      city: "Roseville",
      state: "CA",
      zip: "95661",
      phone: "(916) 782-5534",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from Roseville Galleria",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "roseville-2",
      name: "Rocklin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4845 Granite Dr",
      city: "Rocklin",
      state: "CA",
      zip: "95677",
      phone: "(916) 624-3711",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Rocklin",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "roseville-3",
      name: "Folsom Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 East Bidwell St",
      city: "Folsom",
      state: "CA",
      zip: "95630",
      phone: "(916) 985-4845",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from Folsom Lake College",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for eastern Sacramento County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "roseville-4",
      name: "Citrus Heights Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7730 Auburn Blvd",
      city: "Citrus Heights",
      state: "CA",
      zip: "95610",
      phone: "(916) 726-2664",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from Sunrise Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Citrus Heights and Antelope residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "roseville-5",
      name: "Lincoln Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "845 Joiner Pkwy",
      city: "Lincoln",
      state: "CA",
      zip: "95648",
      phone: "(916) 434-2721",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from downtown Lincoln",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves northern Placer County. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "roseville-6",
      name: "Placer County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2954 Richardson Dr",
      city: "Auburn",
      state: "CA",
      zip: "95603",
      phone: "(530) 886-5600",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from Auburn City Hall",
      website: "https://www.placer.ca.gov/",
      websiteLabel: "Placer County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Placer County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Sacramento area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Placer County applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the SF regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Placer County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Sacramento area travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Roseville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Roseville address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Roseville before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Roseville for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Sacramento area travelers." },
    { q: "How fast can I renew my passport in Roseville?", a: "Fast Passport Center offers fast passport renewal in Roseville in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Roseville in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Roseville?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Roseville residents." },
    { q: "Can I expedite a child passport in Roseville?", a: "Yes. Fast Passport Center expedites child passports in Roseville for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Roseville with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Roseville?",
      a: "With Fast Passport Center's emergency courier service, Roseville travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Roseville?",
      a: "Popular options include the Roseville Post Office (95661), Rocklin Post Office (95677), Folsom Post Office (95630), and Citrus Heights Post Office (95610). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Roseville?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in rural Placer County or near Auburn?",
      a: "We serve all of Placer County including Auburn, Lincoln, Loomis, and Colfax. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Roseville?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Roseville?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Roseville, Rocklin, and Folsom offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Roseville?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Roseville?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Placer County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Roseville", address: "1010 Pleasant Grove Blvd, Roseville, CA 95678", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Rocklin", address: "5400 Crossings Dr, Rocklin, CA 95677", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Roseville", address: "1200 Roseville Pkwy, Roseville, CA 95678", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Folsom", address: "2500 E Bidwell St, Folsom, CA 95630", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Citrus Heights", address: "7010 Auburn Blvd, Citrus Heights, CA 95621", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Lincoln", address: "1350 Ferrari Dr, Lincoln, CA 95648", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Granite Bay", address: "8605 Sierra College Blvd, Granite Bay, CA 95746", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Roseville", address: "9000 Fairway Dr, Roseville, CA 95678", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Roseville",
      address: "7201 Galilee Rd, Roseville, CA 95678",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Rocklin",
      address: "2529 E Vernon St, Roseville, CA 95661",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Folsom",
      address: "323 E Bidwell St, Folsom, CA 95630",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Sacramento",
      address: "4700 Broadway, Sacramento, CA 95820",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service mega center for Sacramento County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "20 miles from Roseville",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "The primary airport for the Sacramento metro area. International flights to Mexico, Canada, and connections to Europe and Asia.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "110 miles from Roseville",
      address: "San Francisco, CA 94128",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-80.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: false,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "90 miles from Roseville",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Alternative Bay Area airport with domestic flights and international connections to Mexico.",
      flightUrl: "https://www.flyoakland.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
  ],
};